import { Component, AfterViewInit, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgFor, NgClass, NgIf } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-playground-container',
  standalone: true,
  imports: [NgFor, NgClass, NgIf, MatTooltipModule],
  templateUrl: './playground-container.component.html',
  styleUrl: './playground-container.component.scss'
})
export class PlaygroundContainerComponent implements AfterViewInit, OnChanges {
  @Input() testResultData: any = null;
  records: any[] = [];

  constructor(private elementRef: ElementRef) {}

  ngAfterViewInit() {
    this.makeColumnsResizable();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['testResultData'] && changes['testResultData'].currentValue) {

      // Process the data immediately when it changes
      this.transformApiDataToRecords();

      // Use setTimeout to ensure DOM is updated before making columns resizable
      setTimeout(() => {
        this.makeColumnsResizable();
      }, 100);
    }
  }

  // Transform API response data to the format expected by the template
  private transformApiDataToRecords() {

    if (!this.testResultData?.result_data) {
      this.records = [];
      return;
    }

    this.records = this.testResultData.result_data.map((item: any) => ({
      contactId: this.formatValue(item.contactId),
      primaryTopic: {
        original: this.formatValue(item.primarytopic?.original_sample),
        test: this.formatValue(item.primarytopic?.test_result),
        is_changed: item.primarytopic?.is_changed === 'true'
      },
      intent: {
        original: this.formatValue(item.intent?.original_sample),
        test: this.formatValue(item.intent?.test_result),
        is_changed: item.intent?.is_changed === 'true'
      },
      topics: {
        original: item.topics?.original_sample || [],
        test: item.topics?.test_result || [],
        is_changed: item.topics?.is_changed === 'true'
      },
      summary: {
        original: this.formatValue(item.summary?.original_sample),
        test: this.formatValue(item.summary?.test_result),
        is_changed: item.summary?.is_changed === 'true'
      },
      rootCause: {
        original: this.formatValue(item.rootcause?.original_sample),
        test: this.formatValue(item.rootcause?.test_result),
        is_changed: item.rootcause?.is_changed === 'true'
      },
      trigger: {
        original: this.formatValue(item.trigger?.original_sample),
        test: this.formatValue(item.trigger?.test_result),
        is_changed: item.trigger?.is_changed === 'true'
      },
      customerPainPoint: {
        original: this.formatValue(item.customerpainpoint?.original_sample),
        test: this.formatValue(item.customerpainpoint?.test_result),
        is_changed: item.customerpainpoint?.is_changed === 'true'
      },
      outcome: {
        original: this.formatValue(item.outcome?.original_sample),
        test: this.formatValue(item.outcome?.test_result),
        is_changed: item.outcome?.is_changed === 'true'
      },
      customerSentimentStart: {
        original: this.formatValue(item.customersentimentatstart?.original_sample),
        test: this.formatValue(item.customersentimentatstart?.test_result),
        is_changed: item.customersentimentatstart?.is_changed === 'true'
      },
      customerSentimentEnd: {
        original: this.formatValue(item.customersentimentatend?.original_sample),
        test: this.formatValue(item.customersentimentatend?.test_result),
        is_changed: item.customersentimentatend?.is_changed === 'true'
      },
      journeyMoment: {
        original: this.formatValue(item.journeymoment?.original_sample),
        test: this.formatValue(item.journeymoment?.test_result),
        is_changed: item.journeymoment?.is_changed === 'true'
      },
      callerType: {
        original: this.formatValue(item.callertype?.original_sample),
        test: this.formatValue(item.callertype?.test_result),
        is_changed: item.callertype?.is_changed === 'true'
      },
      callResolution: {
        original: this.formatValue(item.callresolution?.original_sample),
        test: this.formatValue(item.callresolution?.test_result),
        is_changed: item.callresolution?.is_changed === 'true'
      },
      topicsClustered: {
        original: item.topicsclustered?.original_sample || [],
        test: item.topicsclustered?.test_result || [],
        is_changed: item.topicsclustered?.is_changed === 'true'
      },
      rootCauseClustered: {
        original: item.rootcauseclustered?.original_sample || [],
        test: item.rootcauseclustered?.test_result || [],
        is_changed: item.rootcauseclustered?.is_changed === 'true'
      },
      triggerClustered: {
        original: item.triggerclustered?.original_sample || [],
        test: item.triggerclustered?.test_result || [],
        is_changed: item.triggerclustered?.is_changed === 'true'
      },
      customerPainPointClustered: {
        original: item.customerpainpointclustered?.original_sample || [],
        test: item.customerpainpointclustered?.test_result || [],
        is_changed: item.customerpainpointclustered?.is_changed === 'true'
      },
      outcomeClustered: {
        original: item.outcomeclustered?.original_sample || [],
        test: item.outcomeclustered?.test_result || [],
        is_changed: item.outcomeclustered?.is_changed === 'true'
      }
    }));
  }

  // Helper method to format values: preserve "NA", convert null/undefined to "---"
  private formatValue(value: any): string {
    if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === '') {
      return '---';
    }
    if (value === 'NA' || value === 'na' || value === 'Na') {
      return 'NA';
    }
    return value;
  }

  // Helper method to create tooltip content for topics arrays
  getTopicsTooltip(topics: any[]): string {
    if (!topics || topics.length === 0) {
      return '---';
    }
    return topics.join(', ');
  }

  private makeColumnsResizable() {
    const table = this.elementRef.nativeElement.querySelector('table');
    if (!table) return;

    const headers = table.querySelectorAll('thead th');
    let isResizing = false;
    let currentHeader: HTMLElement | null = null;
    let startX = 0;
    let startWidth = 0;

    headers.forEach((header: HTMLElement) => {
      // Skip the Contact ID column as it's fixed
      if (header.classList.contains('contact-id-header')) return;

      const resizer = document.createElement('div');
      resizer.style.position = 'absolute';
      resizer.style.right = '0';
      resizer.style.top = '0';
      resizer.style.bottom = '0';
      resizer.style.width = '4px';
      resizer.style.cursor = 'col-resize';
      resizer.style.backgroundColor = 'transparent';
      resizer.style.zIndex = '1000';

      header.style.position = 'relative';
      header.appendChild(resizer);

      resizer.addEventListener('mousedown', (e) => {
        isResizing = true;
        currentHeader = header;
        startX = e.pageX;
        startWidth = header.offsetWidth;

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        e.preventDefault();
      });
    });

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing || !currentHeader) return;

      const width = startWidth + (e.pageX - startX);
      if (width >= 300) { // Minimum width constraint
        currentHeader.style.width = width + 'px';
        currentHeader.style.minWidth = width + 'px';
      }
    };

    const onMouseUp = () => {
      isResizing = false;
      currentHeader = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }
}
